<template>
  <div class="l-page l-page--analysis">
    <!-- サイドバーエリア -->
    <page-sidebar />
    <!-- /サイドバーエリア -->
    <main class="l-main">
      <div class="l-main-head">
        <!-- パンくずリスト -->
        <breadcrumb :breadcrumb-data="breadcrumbData" :additional-classes="['p-breadcrumb--analysis']" />
        <!-- /パンくずリスト -->
        <date-range-picker
          ref="refCalendarModal"
          :initial-start-date="calendarStartDate"
          :initial-end-date="calendarEndDate"
          :on-date-range-changed="onDateRangeChanged"
          cancel-button-label="キャンセル"
          enter-button-label="決定"
        />
      </div>
      <div class="l-main-content">
        <div class="p-analysis-prefecture">
          <h2 class="c-title">
            <div class="c-title__text u-fs34">都道府県別での傾向</div>
            <div class="c-date u-w400">
              <ul class="c-date-list">
                <li class="c-date-list__item c-date-list__item--label">表示期間</li>
                <li class="c-date-list__item c-date-list__item--from">{{ displayStartDate }}</li>
                <li class="c-date-list__item c-date-list__item--to">{{ displayEndDate }}</li>
              </ul>
            </div>
          </h2>

          <!-- 指標選択タブ -->
          <analysis-tab
            :current="tab.current"
            :on-select="onTabClick"
          />

          <h3 class="c-title">
            <div class="c-title__text u-fs--l">全ての配信結果に対しての割合</div>
          </h3>

          <!-- 表示（地域別・都道府県別）切り替えタブ -->
          <prefecture-tab
            classes="c-tab--analysis c-tab--prefecture"
            :current="tab.regionType"
            :on-select="onClickPrefectureTab"
          />

          <!-- ヒートマップ -->
          <div
            class="p-analysis-prefecture__heatmap"
            :class="[{'is-loading': isLoadingHeatmap}, {'is-empty': isEmpty}]"
          >
            <div class="c-loader"></div>
            <div class="c-empty">
              <div class="c-empty__text">配信後にデータが反映されます</div>
            </div>
            <div
              class="p-analysis-prefecture__item"
              :class="[{'is-active': tab.regionType == 'region'}]"
            >
              <heatmap-region
                ref="heatmapRegion"
                size="l"
              />
            </div>
            <div
              class="p-analysis-prefecture__item"
              :class="[{'is-active': tab.regionType == 'prefecture'}]"
            >
              <heatmap-prefecture
                ref="heatmapPrefecture"
                size="l"
              />
            </div>
          </div>

          <!-- 配信結果テーブル -->
          <h3 class="c-title c-title--download">
            <div class="c-title__text u-fs--l">全ての配信結果での合計数</div>
            <div class="c-text"><a href="#">CSVデータ ダウンロード</a></div>
          </h3>
          <div
            class="p-analysis-prefecture__data"
            :class="[{'is-loading': isLoadingDataTable}, {'is-empty': isEmpty}]"
          >
            <div class="c-loader"></div>
            <div class="c-empty">
              <div class="c-empty__text">配信後にデータが反映されます</div>
            </div>
            <div
              class="p-analysis-prefecture__item"
              :class="[{'is-active': tab.regionType == 'region'}]"
            >
              <table-region-analysis
                ref="tableRegion"
                classes="c-table--heatmap-prefecture"
                :on-sort-click="onClickTableSortBtn"
              />
            </div>
            <div
              class="p-analysis-prefecture__item"
              :class="[{'is-active': tab.regionType == 'prefecture'}]"
            >
              <table-prefecture-analysis
                ref="tablePrefecture"
                classes="c-table--heatmap-prefecture"
                :on-sort-click="onClickTableSortBtn"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
    <!-- モーダル: エラーメッセージ表示 -->
    <error-modal
      :is-active=isError
      :message=errorMessage
      :on-close-click="onCloseErrorModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

export default {
  props: {
    breadcrumbData: Array,
    displayPeriod: Object,
  },
  data () {
    return {
      active: 'region',
      errorMessage: '',
      isEmpty: false,
      isError: false,
      isLoadingHeatmap: true,
      isLoadingDataTable: true,
      isInitialized: false,
      range: {
        from: this.displayPeriod && this.displayPeriod.from && dayjs(this.displayPeriod.from).isValid()
          ? dayjs(this.displayPeriod.from).toDate()
          : dayjs().add(-30, 'days').toDate(),
        to: this.displayPeriod && this.displayPeriod.to && dayjs(this.displayPeriod.to).isValid()
          ? dayjs(this.displayPeriod.to).toDate()
          : dayjs().toDate(),
      },
      tab: {
        current: 'delivery',
        regionType: 'region',
      },
      data: null,
      sort: {
        key: 'delivery',
        type: 'desc'
      }
    }
  },

  computed: {
    calendarStartDate: function() {
      return dayjs(this.range.from).isValid()
        ? dayjs(this.range.from).format('YYYY-MM-DD')
        : dayjs().add(-30, 'days').format('YYYY-MM-DD')
    },
    calendarEndDate: function() {
      return dayjs(this.range.to).isValid()
        ? dayjs(this.range.to).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
    },
    displayStartDate: function() {
      return dayjs(this.range.from).isValid()
        ? dayjs(this.range.from).format('YYYY/MM/DD')
        : '-'
    },
    displayEndDate: function() {
      return dayjs(this.range.to).isValid()
        ? dayjs(this.range.to).format('YYYY/MM/DD')
        : '-'
    }
  },
  mounted: function() {},

  methods: {
    updateDataset: function() {
      // ローダー表示設定
      this.isLoadingHeatmap = true;
      this.isLoadingDataTable = true;

      // 送信パラメーター作成
      const params = {
        from: dayjs(this.range.from).format('YYYY-MM-DD'),
        to: dayjs(this.range.to).format('YYYY-MM-DD'),
      }

      // ヒートマップ用データの取得処理実行
      axios.get(`/api/analysis/summary-prefecture/${this.tab.current}`, {
        params
      }).then(res => {
        // レスポンスデータの内容がイレギュラーな場合は処理終了
        if (!res.data || !res.data.items) throw new Error('Invalid response data.');

        // 取得データをキャッシュ
        this.data = res.data;

        // 地域別ヒートマップ、ランキングテーブルを更新
        this.$refs.heatmapRegion.init(res.data, this.tab.current, params)
        this.$refs.tableRegion.init(res.data, params, {
          key: this.tab.current,
          type: 'desc'
        }
        )

        // 都道府県別ヒートマップ、ランキングテーブルを更新
        this.$refs.heatmapPrefecture.init(res.data, this.tab.current, params)
        this.$refs.tablePrefecture.init(res.data, params, {
          key: this.tab.current,
          type: 'desc'
        }
        )

        // ローダー非表示設定
        setTimeout(() => {
          this.isLoadingHeatmap = false;
          this.isLoadingDataTable = false;
        }, 300)
      })
        .catch(_e => {
          // エラーモーダル表示
          this.isError = true;
          this.errorMessage = '配信結果データの取得に失敗しました';
          // ローダー非表示設定
          setTimeout(() => {
            this.isLoadingHeatmap = false;
            this.isLoadingDataTable = false;
          }, 300)
        });
    },

    onTabClick: function(selected) {
      // 選択値が設定されていない場合は処理終了
      if (!selected) return;

      // 選択値が現在の選択値値と同じ場合は処理終了
      if (selected == this.tab.current) return

      // 選択値を現在のタブ名に設定
      this.tab.current = selected;

      // データ更新
      this.updateDataset();
    },

    onDateRangeChanged: function(range){
      // 取得データ期間の更新
      this.range.from = range.start;
      this.range.to= range.end;

      // データ更新
      this.updateDataset();
    },

    // -----------------------------------------------------------------------------------------------------
    // エミット系メソッド一覧: 子孫のUI操作にフックエミットコールされるか、引き渡した関数オブジェクトからコールされるメソッド
    // -----------------------------------------------------------------------------------------------------
    // クリック: エラーモーダルを閉じる処理
    onCloseErrorModal: function() {
      this.isError = false
      this.errorMessage = ''
    },

    // クリック: 都道府県タブをクリックした時
    onClickPrefectureTab: function(selected) {
      this.tab.regionType = selected
    },

    // クリック: テーブルソートボタン
    onClickTableSortBtn: function(sort) {
      // 送信パラメーター作成 (* メソッドの呼び出しに必要なため
      const params = {
        from: dayjs(this.range.from).format('YYYY-MM-DD'),
        to: dayjs(this.range.to).format('YYYY-MM-DD'),
      }

      // ソート状態を更新
      this.sort = {
        key: sort.key,
        type: sort.type,
      };

      // ローダー表示設定
      this.isLoadingDataTable = true;

      // テーブルデータの更新
      this.$refs.tableRegion.init(this.data, params, {
        key: this.sort.key,
        type: this.sort.type,
      }
      )
      this.$refs.tablePrefecture.init(this.data, params, {
        key: this.sort.key,
        type: this.sort.type,
      }
      )
      // ローダー非表示設定
      setTimeout(() => {
        this.isLoadingDataTable = false;
      }, 300)
    }
  }
}
</script>
